import React from "react";
import NavbarClass from "../NavbarClass";
import { localAPI } from "../../api/local";
import { Redirect } from "react-router-dom";
import { Helmet } from "react-helmet";

class AdminUsersPage extends React.Component {
  state = {
    // Start as null (unknown) — prevents a flash of page content before the
    // admin check completes (avoids brief render before potential redirect)
    isAdmin: null,
    isDownloading: false,
    downloadError: null,
  };

  async componentDidMount() {
    await localAPI
      .get("/api/users/isadmin")
      .then((data) => {
        this.setState({ isAdmin: !!data.data.success });
      })
      .catch(() => {
        this.setState({ isAdmin: false });
      });
  }

  handleDownloadReport = async () => {
    this.setState({ isDownloading: true, downloadError: null });

    try {
      const response = await localAPI.get("/api/admin/report", {
        responseType: "blob",
      });

      // Build object URL from blob
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      // Resolve filename from Content-Disposition header if accessible (same-origin
      // or when Access-Control-Expose-Headers is set), else use dated fallback
      const contentDisposition = response.headers["content-disposition"];
      let filename = `users_report_${new Date().toISOString().split("T")[0]}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) filename = match[1].trim();
      }

      // Trigger browser download via a temporary anchor element
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Delay revocation to give the browser time to start the download
      // (synchronous revoke can cancel the download in some browsers)
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (err) {
      this.setState({
        downloadError:
          "Failed to download the report. Please try again or check console for details.",
      });
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  render() {
    const { isAdmin, isDownloading, downloadError } = this.state;

    // Still waiting for the admin check — render nothing to avoid flash
    if (isAdmin === null) return null;

    // Not an admin — redirect immediately
    if (!isAdmin) return <Redirect to="/" />;

    return (
      <div>
        {/* Inject @keyframes for the spinner — inline styles can't define keyframes */}
        <style>{`
          @keyframes adminSpinnerSpin {
            to { transform: rotate(360deg); }
          }
        `}</style>

        <Helmet>
          <meta name="robots" content="noindex" />
          <title>Admin — User Report | MeenduToons</title>
        </Helmet>

        <NavbarClass handleSignInOpen={this.props.handleSignInOpen} />

        <div style={styles.container}>
          <h2 style={styles.heading}>User Report</h2>
          <p style={styles.description}>
            Download a comprehensive XLSX report containing all user profiles,
            watchlists (with video details), and subscription information.
          </p>

          <button
            id="admin-download-report-btn"
            style={{
              ...styles.downloadBtn,
              ...(isDownloading ? styles.downloadBtnDisabled : {}),
            }}
            onClick={this.handleDownloadReport}
            disabled={isDownloading}
            aria-busy={isDownloading}
          >
            {isDownloading ? (
              <>
                <span style={styles.spinner} aria-hidden="true" />
                Generating Report…
              </>
            ) : (
              <>⬇&nbsp; Download User Report (XLSX)</>
            )}
          </button>

          {downloadError && (
            <div style={styles.errorBox} role="alert">
              ⚠️ {downloadError}
            </div>
          )}

          <div style={styles.infoBox}>
            <p style={styles.infoTitle}>The report contains 3 sheets:</p>
            <ul style={styles.infoList}>
              <li>
                <strong>Users Overview</strong> — Name, email, phone, login
                type, join date, premium &amp; admin status, daily mail
                preference, watchlist count
              </li>
              <li>
                <strong>Watchlist</strong> — Per-user watchlist items with
                Video ID, Title, Video URL, and Thumbnail URL (deduplicated)
              </li>
              <li>
                <strong>Subscriptions</strong> — Plan name (from Razorpay),
                type (Recurring / One-Time), start &amp; end dates,
                cancellation and pause status, Razorpay IDs
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  container: {
    maxWidth: "720px",
    margin: "48px auto",
    padding: "0 24px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  heading: {
    fontSize: "26px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#1a1a2e",
  },
  description: {
    color: "#555",
    fontSize: "15px",
    marginBottom: "28px",
    lineHeight: "1.6",
  },
  downloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 28px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#fff",
    background: "linear-gradient(135deg, #2F5496, #1e3a6e)",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(47,84,150,0.35)",
    transition: "opacity 0.2s",
  },
  downloadBtnDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  // @keyframes are injected via <style> tag above; animation name matches exactly
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "adminSpinnerSpin 0.7s linear infinite",
    flexShrink: 0,
  },
  errorBox: {
    marginTop: "18px",
    padding: "12px 16px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    color: "#991b1b",
    fontSize: "14px",
  },
  infoBox: {
    marginTop: "36px",
    padding: "20px 24px",
    background: "#f0f4ff",
    border: "1px solid #c7d4f7",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#333",
  },
  infoTitle: {
    fontWeight: "600",
    marginBottom: "10px",
    fontSize: "15px",
    margin: "0 0 10px 0",
  },
  infoList: {
    paddingLeft: "20px",
    lineHeight: "1.9",
    margin: 0,
  },
};

export default AdminUsersPage;
